/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  MenuItem,
  Select,
  InputLabel,
  InputAdornment,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add,
  Delete,
  Edit,
  Send,
  ClearAll,
  WarningAmber,
  Clear,
  Business,
  AssignmentTurnedIn,
  AddShoppingCart,
  ShoppingCartCheckout,
  ShoppingCart,
  Search,
  Rule,
} from '@mui/icons-material';

import api from '../../../../../services/api';
import GenericSearchInput from '../../../../../components/common/GenericSearchInput';
import ErpAuthModal from '../../../../../components/common/ErpAuthModal';

export interface Branch {
  id: number;
  nome: string;
  sigla?: string;
}

export interface Company {
  id: number;
  nome: string;
  subtitulo?: string;
  filiais?: Branch[];
}

export interface PurchaseRequestGridItem {
  tempId: string;
  itemType: 'P' | 'S';
  itemCode: string;
  itemDescription?: string;
  derivation?: string;
  derivationDescription?: string;
  quantity: number;
  price?: number;
  unitMeasure: string;
  depositCode: string;
  transactionCode: string;
  costCenter: string;
  costCenterDescription?: string;
  expectedDate: string;
  observation: string;
  isEmergency?: boolean;
}

export interface ItemProcessResult {
  tempId: string;
  seqSol: number;
  itemCode: string;
  itemType: 'P' | 'S';
  status: 'success' | 'error';
  numSol?: string | null;
  message: string;
}

const STORAGE_GRID_KEY = 'purchase_request_draft_grid';

const addDaysToDate = (baseDate: Date, days: number): Date => {
  const result = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate() + days, 12, 0, 0);
  return result;
};

const formatDateToYYYYMMDD = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateToPtBR = (dateStr: string): string => {
  if (!dateStr) return '';
  const clean = dateStr.split('T')[0];
  const parts = clean.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
};

const formatCurrencyPtBR = (num?: number) => {
  if (num === undefined || num === null || Number.isNaN(Number(num))) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(num));
};

export default function PurchaseRequestCreatePage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();

  const handleNavigate = (path: string) => {
    const target = path.startsWith('/') ? path : `/${path}`;
    if (typeof navigate === 'function') {
      navigate(target);
    } else {
      window.location.href = target;
    }
  };

  const fieldRefs = {
    itemCode: useRef<HTMLInputElement>(null),
    derivation: useRef<HTMLInputElement>(null),
    costCenter: useRef<HTMLInputElement>(null),
    depositCode: useRef<HTMLInputElement>(null),
    transactionCode: useRef<HTMLInputElement>(null),
    expectedDate: useRef<HTMLInputElement>(null),
    quantity: useRef<HTMLInputElement>(null),
    price: useRef<HTMLInputElement>(null),
    observation: useRef<HTMLInputElement>(null),
    addButton: useRef<HTMLButtonElement>(null),
  };

  const [hoveredTempId, setHoveredTempId] = useState<string | null>(null);

  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState<boolean>(false);

  const [companyId, setCompanyId] = useState<number>(1);
  const [branchId, setBranchId] = useState<number>(1);

  const [pendingCompanyId, setPendingCompanyId] = useState<number | null>(null);
  const [pendingBranchId, setPendingBranchId] = useState<number | null>(null);
  const [showCompanyConfirmDialog, setShowCompanyConfirmDialog] = useState<boolean>(false);

  const defaultExpectedDate = useMemo(() => {
    const today = new Date();
    const newToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12, 0, 0);
    const futureDate = addDaysToDate(newToday, 11);

    return formatDateToYYYYMMDD(futureDate);
  }, []);

  const [itemType, setItemType] = useState<'P' | 'S'>('P');
  const [itemCode, setItemCode] = useState<string>('');
  const [derivation, setDerivation] = useState<string>('');
  const [quantity, setQuantity] = useState<string>('1');
  const [price, setPrice] = useState<string>('');
  const [unitMeasure, setUnitMeasure] = useState<string>('');
  const [depositCode, setDepositCode] = useState<string>('');
  // Inicializa com 91400 (Padrão para Produto)
  const [transactionCode, setTransactionCode] = useState<string>('91400');
  const [costCenter, setCostCenter] = useState<string>('');
  const [expectedDate, setExpectedDate] = useState<string>(defaultExpectedDate);
  const [observation, setObservation] = useState<string>('');

  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [isValidatingItem, setIsValidatingItem] = useState<boolean>(false);

  const [showConfirmSendDialog, setShowConfirmSendDialog] = useState<boolean>(false);
  const [authModalOpen, setAuthModalOpen] = useState<boolean>(false);
  const [resultModal, setResultModal] = useState<{
    open: boolean;
    results: ItemProcessResult[];
  }>({
    open: false,
    results: [],
  });

  useEffect(() => {
    const checkErpCredentials = async () => {
      try {
        const { data } = await api.get('erp/auth/status');
        if (!data || !data.authenticated) {
          showNotification('Atenção: Autentique-se no ERP Sapiens para continuar.', 'info');
          setAuthModalOpen(true);
        }
      } catch (error) {
        console.error('Erro ao checar autenticação ERP:', error);
        setAuthModalOpen(true);
      }
    };

    checkErpCredentials();
  }, []);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setIsLoadingCompanies(true);
        const { data } = await api.get<Company[]>('app/data-collection/get-company');
        if (data && Array.isArray(data) && data.length > 0) {
          setCompanies(data);
          const firstComp = data[0];
          setCompanyId(firstComp.id);

          if (firstComp.filiais && firstComp.filiais.length > 0) {
            setBranchId(firstComp.filiais[0].id);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar empresas e filiais:', error);
        showNotification('Erro ao carregar lista de empresas.', 'error');
      } finally {
        setIsLoadingCompanies(false);
      }
    };

    fetchCompanies();
  }, []);

  // Transição de tipo: Produto <-> Serviço com atualização da transação
  const handleItemTypeChange = (newType: 'P' | 'S') => {
    setItemType(newType);
    setItemCode('');
    setDerivation('');
    setDepositCode('');
    setUnitMeasure('');
    setPrice('');
    setFieldErrors({});

    // Produto = 91400 | Serviço = 91406
    if (newType === 'P') {
      setTransactionCode('91400');
    } else {
      setTransactionCode('91406');
    }
  };

  const handleItemCodeSelect = async (code: string) => {
    const formattedCode = code.toUpperCase();
    setItemCode(formattedCode);
    setDerivation('');
    setFieldErrors(prev => ({ ...prev, itemCode: '' }));

    if (!formattedCode.trim()) {
      setDepositCode('');
      setUnitMeasure('');
      return;
    }

    try {
      const { data } = await api.get('erp/purchases/requests/item-info', {
        params: { companyId, itemType, itemCode: formattedCode.trim() },
      });

      if (data) {
        if (data.depPad) setDepositCode(data.depPad);
        if (data.uniMed) setUnitMeasure(data.uniMed);
      }
    } catch (error) {
      console.error('Erro ao buscar detalhes do item:', error);
    }
  };

  const currentBranches = useMemo(() => {
    const found = companies.find(c => Number(c.id) === Number(companyId));
    return found?.filiais || [];
  }, [companies, companyId]);

  const [gridItems, setGridItems] = useState<PurchaseRequestGridItem[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_GRID_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_GRID_KEY, JSON.stringify(gridItems));
    } catch (e) {
      console.error('Erro ao salvar rascunho da tabela:', e);
    }
  }, [gridItems]);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [emergencyAlert, setEmergencyAlert] = useState<{
    open: boolean;
    pendingItem: PurchaseRequestGridItem | null;
  }>({
    open: false,
    pendingItem: null,
  });

  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    type: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    type: 'info',
  });

  const showNotification = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setNotification({ open: true, message, type });
  };

  const handleResetFormItem = () => {
    setItemCode('');
    setDerivation('');
    setQuantity('1');
    setPrice('');
    setUnitMeasure('');
    setDepositCode('');
    setObservation('');
    setCostCenter('');
    setExpectedDate(defaultExpectedDate);
    setFieldErrors({});
    setTransactionCode(itemType === 'P' ? '91400' : '91406');
  };

  const handleCompanySelectChange = (newCompId: number) => {
    if (gridItems.length > 0) {
      setPendingCompanyId(newCompId);
      setPendingBranchId(null);
      setShowCompanyConfirmDialog(true);
    } else {
      setCompanyId(newCompId);
      const found = companies.find(c => Number(c.id) === Number(newCompId));
      if (found?.filiais && found.filiais.length > 0) {
        setBranchId(found.filiais[0].id);
      }
      handleResetFormItem();
    }
  };

  const handleBranchSelectChange = (newBranchId: number) => {
    if (gridItems.length > 0) {
      setPendingBranchId(newBranchId);
      setPendingCompanyId(null);
      setShowCompanyConfirmDialog(true);
    } else {
      setBranchId(newBranchId);
      handleResetFormItem();
    }
  };

  const confirmCompanyBranchChange = () => {
    if (pendingCompanyId !== null) {
      setCompanyId(pendingCompanyId);
      const found = companies.find(c => Number(c.id) === Number(pendingCompanyId));
      if (found?.filiais && found.filiais.length > 0) {
        setBranchId(found.filiais[0].id);
      }
    }
    if (pendingBranchId !== null) {
      setBranchId(pendingBranchId);
    }

    setGridItems([]);
    localStorage.removeItem(STORAGE_GRID_KEY);
    handleResetFormItem();
    setShowCompanyConfirmDialog(false);
    setPendingCompanyId(null);
    setPendingBranchId(null);
    showNotification('Empresa/Filial alterada. Tabela limpa.', 'info');
  };

  const insertItemToGrid = (item: PurchaseRequestGridItem) => {
    setGridItems(prev => [...prev, item]);
    handleResetFormItem();
    showNotification('Item adicionado à tabela com sucesso.', 'info');
  };

  const handleValidateAndAddItem = async () => {
    const errors: { [key: string]: string } = {};

    if (!itemCode.trim()) errors.itemCode = 'Selecione ou digite um item.';

    if (itemType === 'P') {
      if (!derivation.trim()) errors.derivation = 'A derivação é obrigatória.';
      if (!depositCode.trim()) errors.depositCode = 'Selecione ou digite o depósito.';
    }

    // Preço é obrigatório APENAS para Serviços
    if (itemType === 'S') {
      const numPrice = Number(price.replace(',', '.'));
      if (!price || Number.isNaN(numPrice) || numPrice <= 0) {
        errors.price = 'Informe o valor estimado do serviço.';
      }
    }

    if (!transactionCode.trim()) errors.transactionCode = 'Selecione ou digite a transação.';
    if (!costCenter.trim()) errors.costCenter = 'Selecione ou digite um centro de custo.';
    if (!expectedDate) errors.expectedDate = 'Informe a data prevista.';
    if (!quantity || Number(quantity) <= 0) errors.quantity = 'Informe uma quantidade válida.';

    if (!observation.trim()) {
      errors.observation = 'A observação do item é obrigatória.';
    } else if (observation.length > 250) {
      errors.observation = 'A observação deve ter no máximo 250 caracteres.';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      showNotification('Verifique os campos destacados em vermelho.', 'error');
      return;
    }

    try {
      setIsValidatingItem(true);
      const { data } = await api.post('erp/purchases/requests/validate-item', {
        companyId,
        itemType,
        itemCode: itemCode.trim().toUpperCase(),
        derivation: itemType === 'P' ? derivation.trim().toUpperCase() : undefined,
        costCenter: costCenter.trim().toUpperCase(),
      });

      if (!data.valid) {
        setFieldErrors(data.errors || {});
        showNotification('Dados não encontrados no banco de dados ERP.', 'error');
        return;
      }

      setFieldErrors({});

      const resolvedUniMed = unitMeasure || data.descriptions?.unitMeasure || (itemType === 'S' ? 'UN' : 'UN');
      const isEmergency = expectedDate < defaultExpectedDate;
      const parsedPrice = itemType === 'S' && price ? Number(price.replace(',', '.')) : 0;

      const newItem: PurchaseRequestGridItem = {
        tempId: `${Date.now()}-${Math.random()}`,
        itemType,
        itemCode: itemCode.trim().toUpperCase(),
        itemDescription: data.descriptions?.itemDescription || '',
        derivation: itemType === 'P' ? derivation.trim().toUpperCase() : '',
        derivationDescription: data.descriptions?.derivationDescription || '',
        quantity: Number(quantity),
        price: parsedPrice,
        unitMeasure: resolvedUniMed,
        depositCode: itemType === 'P' ? depositCode.trim().toUpperCase() : '',
        transactionCode: transactionCode.trim().toUpperCase(),
        costCenter: costCenter.trim().toUpperCase(),
        costCenterDescription: data.descriptions?.costCenterDescription || '',
        expectedDate,
        observation: observation.trim(),
        isEmergency,
      };

      if (isEmergency) {
        setEmergencyAlert({ open: true, pendingItem: newItem });
        return;
      }

      insertItemToGrid(newItem);
    } catch (error: any) {
      console.error('Erro na validação do item:', error);
      showNotification('Erro ao validar existência dos dados no ERP.', 'error');
    } finally {
      setIsValidatingItem(false);
    }
  };

  const handleEditItem = (item: PurchaseRequestGridItem) => {
    setItemType(item.itemType);
    setItemCode(item.itemCode);
    setDerivation(item.derivation || '');
    setQuantity(String(item.quantity));
    setPrice(item.itemType === 'S' && item.price ? String(item.price) : '');
    setUnitMeasure(item.unitMeasure);
    setDepositCode(item.depositCode || '');
    setTransactionCode(item.transactionCode || (item.itemType === 'P' ? '91400' : '91406'));
    setCostCenter(item.costCenter);
    setExpectedDate(item.expectedDate);
    setObservation(item.observation);

    setGridItems(prev => prev.filter(i => i.tempId !== item.tempId));
    setFieldErrors({});
    showNotification('Item removido da tabela e carregado para edição.', 'info');
  };

  const handleRemoveItem = (tempId: string) => {
    setGridItems(prev => prev.filter(item => item.tempId !== tempId));
  };

  const handleClearAll = () => {
    setGridItems([]);
    localStorage.removeItem(STORAGE_GRID_KEY);
  };

  const handleOpenSendConfirmation = () => {
    if (gridItems.length === 0) {
      showNotification('Adicione ao menos um item na tabela antes de gerar.', 'error');
      return;
    }
    setShowConfirmSendDialog(true);
  };

  const handleConfirmSendToWebservice = async () => {
    setShowConfirmSendDialog(false);

    try {
      setIsSubmitting(true);
      const { data } = await api.post('erp/purchases/requests/create', {
        companyId,
        branchId,
        items: gridItems,
      });

      const results: ItemProcessResult[] = data.results || [];

      setResultModal({
        open: true,
        results,
      });

      const successfulTempIds = results.filter(r => r.status === 'success').map(r => r.tempId);

      setGridItems(prev => prev.filter(item => !successfulTempIds.includes(item.tempId)));
    } catch (error: any) {
      console.error('Erro ao enviar solicitações:', error);
      const status = error?.response?.status;
      const msg = error?.response?.data?.message || 'Ocorreu um erro na comunicação com o Webservice ERP Sapiens.';

      if (
        status === 401 ||
        msg.toLowerCase().includes('credenciais') ||
        msg.toLowerCase().includes('erp_login') ||
        msg.toLowerCase().includes('erp_password') ||
        msg.toLowerCase().includes('autentic')
      ) {
        showNotification('Autenticação do ERP necessária. Informe suas credenciais.', 'error');
        setAuthModalOpen(true);
      } else {
        showNotification(msg, 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDownEnter = (e: React.KeyboardEvent, nextTargetRef?: React.RefObject<any>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (nextTargetRef && nextTargetRef.current) {
        nextTargetRef.current.focus();
      }
    }
  };

  const selectedCompanyName = useMemo(() => {
    const comp = companies.find(c => Number(c.id) === Number(companyId));
    return comp ? `${comp.id} - ${comp.nome}` : String(companyId);
  }, [companies, companyId]);

  const selectedBranchName = useMemo(() => {
    const branch = currentBranches.find(b => Number(b.id) === Number(branchId));
    return branch ? `${branch.id} - ${branch.nome}` : String(branchId);
  }, [currentBranches, branchId]);

  return (
    <Box pb={3}>
      {/* CABEÇALHO COM TÍTULO E BOTÕES DE NAVEGAÇÃO ALINHADOS À DIREITA */}
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={1.5}>
        <Box display="flex" alignItems="center" gap={1}>
          <ShoppingCart color="inherit" fontSize="large" />
          <Typography variant="h5" fontWeight="bold" color="text.primary">
            Geração de Solicitação de Compra
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<Search />}
            onClick={() => handleNavigate('erp/purchases/purchase/search')}
            sx={{ fontWeight: 'bold', textTransform: 'none', height: 38 }}
          >
            Consultar Solicitações
          </Button>

          <Button
            variant="outlined"
            color="warning"
            startIcon={<Rule />}
            onClick={() => handleNavigate('erp/purchases/purchase/approval')}
            sx={{ fontWeight: 'bold', textTransform: 'none', height: 38 }}
          >
            Aprovação de Solicitações
          </Button>
        </Stack>
      </Box>

      <Typography variant="body2" color="text.secondary" mb={3}>
        Selecione a empresa/filial, adicione os produtos ou serviços na tabela e clique em <b>Gerar Solicitação</b>.
      </Typography>

      {/* SELEÇÃO DE EMPRESA E FILIAL */}
      <Paper elevation={2} sx={{ p: 2, mb: 2.5, borderRadius: 2, bgcolor: 'background.paper' }}>
        <Box display="flex" alignItems="center" gap={1} mb={1.5}>
          <Business color="primary" fontSize="small" />
          <Typography variant="subtitle2" fontWeight="bold" color="primary">
            Empresa e Filial de Destino
          </Typography>
          {isLoadingCompanies && <CircularProgress size={16} sx={{ ml: 1 }} />}
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="select-company-label">Empresa *</InputLabel>
              <Select
                labelId="select-company-label"
                value={companyId}
                label="Empresa"
                required
                disabled={isLoadingCompanies || companies.length === 0}
                onChange={e => handleCompanySelectChange(Number(e.target.value))}
              >
                {companies.map(comp => (
                  <MenuItem key={comp.id} value={comp.id}>
                    {comp.id} - {comp.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="select-branch-label">Filial *</InputLabel>
              <Select
                labelId="select-branch-label"
                value={branchId}
                label="Filial"
                required
                disabled={isLoadingCompanies || currentBranches.length === 0}
                onChange={e => handleBranchSelectChange(Number(e.target.value))}
              >
                {currentBranches.map(branch => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* PAINEL DE INCLUSÃO DE ITEM */}
      <Paper elevation={2} sx={{ p: 2.5, mb: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
        <Box display="flex" alignItems="center" gap={1} mb={1.5}>
          <AddShoppingCart color="primary" fontSize="small" />
          <Typography variant="subtitle2" fontWeight="bold" color="primary">
            Adicionar Item à Solicitação
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl component="fieldset" size="small">
              <FormLabel component="legend" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                Tipo *
              </FormLabel>
              <RadioGroup row value={itemType} onChange={e => handleItemTypeChange(e.target.value as 'P' | 'S')}>
                <FormControlLabel value="P" control={<Radio size="small" />} label="Produto" />
                <FormControlLabel value="S" control={<Radio size="small" />} label="Serviço" />
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={itemType === 'P' ? 3 : 4} md={itemType === 'P' ? 3 : 4}>
            <GenericSearchInput
              label={itemType === 'P' ? 'Produto *' : 'Serviço *'}
              source={itemType === 'P' ? 'products' : 'services'}
              companyId={companyId}
              value={itemCode}
              inputRef={fieldRefs.itemCode}
              onKeyDown={e => handleKeyDownEnter(e, itemType === 'P' ? fieldRefs.derivation : fieldRefs.costCenter)}
              onChange={handleItemCodeSelect}
              multiple={false}
              error={Boolean(fieldErrors.itemCode)}
              helperText={fieldErrors.itemCode}
              placeholder="Código..."
            />
          </Grid>

          {itemType === 'P' && (
            <Grid item xs={12} sm={2} md={2}>
              <GenericSearchInput
                label="Derivação *"
                source="derivation"
                companyId={companyId}
                productId={itemCode}
                value={derivation}
                inputRef={fieldRefs.derivation}
                onKeyDown={e => handleKeyDownEnter(e, fieldRefs.costCenter)}
                onChange={val => {
                  setDerivation(val.toUpperCase());
                  setFieldErrors(prev => ({ ...prev, derivation: '' }));
                }}
                multiple={false}
                disabled={!itemCode.trim()}
                error={Boolean(fieldErrors.derivation)}
                helperText={fieldErrors.derivation}
                placeholder={itemCode.trim() ? 'Código...' : 'Selecione o produto'}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={itemType === 'P' ? 2 : 3} md={itemType === 'P' ? 2 : 3}>
            <GenericSearchInput
              label="Centro de Custo *"
              source="costCenter"
              companyId={companyId}
              value={costCenter}
              inputRef={fieldRefs.costCenter}
              onKeyDown={e =>
                handleKeyDownEnter(e, itemType === 'P' ? fieldRefs.depositCode : fieldRefs.transactionCode)
              }
              onChange={val => {
                setCostCenter(val.toUpperCase());
                setFieldErrors(prev => ({ ...prev, costCenter: '' }));
              }}
              multiple={false}
              error={Boolean(fieldErrors.costCenter)}
              helperText={fieldErrors.costCenter}
              placeholder="Código..."
            />
          </Grid>

          {itemType === 'P' && (
            <Grid item xs={12} sm={2} md={2}>
              <GenericSearchInput
                label="Depósito *"
                source="deposits"
                companyId={companyId}
                onlyActive={true}
                value={depositCode}
                inputRef={fieldRefs.depositCode}
                onKeyDown={e => handleKeyDownEnter(e, fieldRefs.transactionCode)}
                onChange={val => {
                  setDepositCode(val.toUpperCase());
                  setFieldErrors(prev => ({ ...prev, depositCode: '' }));
                }}
                multiple={false}
                error={Boolean(fieldErrors.depositCode)}
                helperText={fieldErrors.depositCode}
                placeholder="Código..."
              />
            </Grid>
          )}

          <Grid item xs={12} sm={itemType === 'P' ? 3 : 2} md={itemType === 'P' ? 3 : 2}>
            <GenericSearchInput
              label="Transação *"
              source="transactions"
              companyId={companyId}
              module={itemType === 'P' ? 'CSO' : 'CSO'}
              onlyActive={true}
              value={transactionCode}
              inputRef={fieldRefs.transactionCode}
              onKeyDown={e => handleKeyDownEnter(e, fieldRefs.expectedDate)}
              onChange={val => {
                setTransactionCode(val.toUpperCase());
                setFieldErrors(prev => ({ ...prev, transactionCode: '' }));
              }}
              multiple={false}
              error={Boolean(fieldErrors.transactionCode)}
              helperText={fieldErrors.transactionCode}
              placeholder="Código..."
            />
          </Grid>

          <Grid item xs={6} sm={2} md={itemType === 'P' ? 2 : 1.5}>
            <TextField
              fullWidth
              size="small"
              required
              type="date"
              label="Data Prevista"
              inputRef={fieldRefs.expectedDate}
              value={expectedDate}
              error={Boolean(fieldErrors.expectedDate)}
              helperText={fieldErrors.expectedDate}
              onChange={e => {
                setExpectedDate(e.target.value);
                setFieldErrors(prev => ({ ...prev, expectedDate: '' }));
              }}
              onKeyDown={e => handleKeyDownEnter(e, fieldRefs.quantity)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={6} sm={2} md={itemType === 'P' ? 2 : 1.5}>
            <TextField
              fullWidth
              size="small"
              required
              type="number"
              label="Quantidade"
              inputRef={fieldRefs.quantity}
              value={quantity}
              error={Boolean(fieldErrors.quantity)}
              helperText={fieldErrors.quantity}
              onChange={e => {
                setQuantity(e.target.value);
                setFieldErrors(prev => ({ ...prev, quantity: '' }));
              }}
              onKeyDown={e => handleKeyDownEnter(e, itemType === 'S' ? fieldRefs.price : fieldRefs.observation)}
              InputProps={{
                inputProps: {
                  min: 0.0001,
                  step: 'any',
                  style: { textAlign: 'right' },
                },
                endAdornment: unitMeasure ? (
                  <InputAdornment position="end">
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color="primary.main"
                      sx={{ userSelect: 'none', ml: 0.5 }}
                    >
                      {unitMeasure}
                    </Typography>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Grid>

          {/* CAMPO DE VALOR ESTIMADO DO SERVIÇO (EXIBIDO EXCLUSIVAMENTE PARA SERVIÇO) */}
          {itemType === 'S' && (
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                required
                label="Preço Estimado *"
                inputRef={fieldRefs.price}
                value={price}
                placeholder="0,00"
                error={Boolean(fieldErrors.price)}
                helperText={fieldErrors.price}
                onChange={e => {
                  setPrice(e.target.value);
                  setFieldErrors(prev => ({ ...prev, price: '' }));
                }}
                onKeyDown={e => handleKeyDownEnter(e, fieldRefs.observation)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography variant="body2" color="text.secondary">
                        R$
                      </Typography>
                    </InputAdornment>
                  ),
                  inputProps: {
                    style: { textAlign: 'right' },
                  },
                }}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              size="small"
              required
              label="Observação do Item"
              inputRef={fieldRefs.observation}
              placeholder="Descreva a justificativa ou detalhes da solicitação..."
              value={observation}
              error={Boolean(fieldErrors.observation)}
              helperText={fieldErrors.observation || `${observation.length}/250 caracteres`}
              inputProps={{ maxLength: 250 }}
              onChange={e => {
                if (e.target.value.length <= 250) {
                  setObservation(e.target.value);
                  setFieldErrors(prev => ({ ...prev, observation: '' }));
                }
              }}
              onKeyDown={e => handleKeyDownEnter(e, fieldRefs.addButton)}
            />
          </Grid>

          <Grid item xs={12} display="flex" justifyContent="flex-end" sx={{ pt: 1 }}>
            <Stack direction="row" spacing={1.5}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleResetFormItem}
                startIcon={<Clear />}
                sx={{ height: 40, fontWeight: 'bold', px: 3 }}
              >
                Limpar
              </Button>
              <Button
                ref={fieldRefs.addButton}
                variant="contained"
                color="primary"
                onClick={handleValidateAndAddItem}
                disabled={isValidatingItem}
                startIcon={isValidatingItem ? <CircularProgress size={18} color="inherit" /> : <Add />}
                sx={{ height: 40, fontWeight: 'bold', px: 4 }}
              >
                Adicionar
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* TABELA DE ITENS */}
      <Paper elevation={2} sx={{ borderRadius: 2, bgcolor: 'background.paper', overflow: 'hidden' }}>
        <Box p={2} display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1} mb={1.5}>
            <ShoppingCartCheckout color="primary" fontSize="small" />
            <Typography variant="subtitle2" fontWeight="bold" color="primary">
              Itens da Solicitação ({gridItems.length})
            </Typography>
          </Box>

          {gridItems.length > 0 && (
            <Button
              size="small"
              color="error"
              startIcon={<ClearAll />}
              onClick={handleClearAll}
              disabled={isSubmitting}
            >
              Limpar Tabela
            </Button>
          )}
        </Box>

        <TableContainer sx={{ maxHeight: '50vh' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: isDark ? 'grey.900' : 'grey.100', fontWeight: 'bold' } }}>
                <TableCell width={80}>Tipo</TableCell>
                <TableCell>Código / Item</TableCell>
                <TableCell width={110}>Derivação</TableCell>
                <TableCell width={140}>Centro de Custo</TableCell>
                <TableCell width={90}>Depósito</TableCell>
                <TableCell width={90}>Transação</TableCell>
                <TableCell width={110}>Data Prevista</TableCell>
                <TableCell align="right" width={100}>
                  Qtd / UM
                </TableCell>
                <TableCell align="right" width={110}>
                  Preço Estimado
                </TableCell>
                <TableCell align="center" width={80}>
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {gridItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Nenhum item pendente na tabela. Preencha os campos acima e clique em <b>Adicionar</b>.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                gridItems.map(row => {
                  const isRowHovered = hoveredTempId === row.tempId;
                  const hoverBgColor = isRowHovered
                    ? isDark
                      ? 'rgba(255, 255, 255, 0.08)'
                      : 'rgba(0, 0, 0, 0.04)'
                    : 'transparent';

                  const isUrgent = row.isEmergency || row.expectedDate < defaultExpectedDate;

                  return (
                    <React.Fragment key={row.tempId}>
                      <TableRow
                        onMouseEnter={() => setHoveredTempId(row.tempId)}
                        onMouseLeave={() => setHoveredTempId(null)}
                        sx={{
                          bgcolor: hoverBgColor,
                          transition: 'background-color 0.15s ease',
                          '& td': { borderBottom: 'none', pt: 1.5, pb: 0.5 },
                        }}
                      >
                        <TableCell>
                          <Chip
                            label={row.itemType === 'P' ? 'Produto' : 'Serviço'}
                            size="small"
                            color={row.itemType === 'P' ? 'primary' : 'secondary'}
                            variant="outlined"
                            sx={{ fontWeight: 'bold', height: 20, fontSize: '0.7rem' }}
                          />
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {row.itemCode}
                          </Typography>
                          {row.itemDescription && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {row.itemDescription}
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {row.derivation || '-'}
                          </Typography>
                          {row.derivationDescription && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {row.derivationDescription}
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {row.costCenter}
                          </Typography>
                          {row.costCenterDescription && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {row.costCenterDescription}
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {row.depositCode || '-'}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {row.transactionCode}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Box display="flex" flexDirection="column" alignItems="flex-start">
                            <Typography variant="body2" fontWeight="bold">
                              {formatDateToPtBR(row.expectedDate)}
                            </Typography>
                            {isUrgent && (
                              <Chip
                                label="Urgente"
                                color="error"
                                size="small"
                                sx={{ fontWeight: 'bold', height: 18, fontSize: '0.65rem', mt: 0.5 }}
                              />
                            )}
                          </Box>
                        </TableCell>

                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          {row.quantity} {row.unitMeasure || ''}
                        </TableCell>

                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {row.itemType === 'S' && row.price && row.price > 0 ? formatCurrencyPtBR(row.price) : '-'}
                        </TableCell>

                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="Editar Item">
                              <IconButton size="small" color="primary" onClick={() => handleEditItem(row)}>
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remover Item">
                              <IconButton size="small" color="error" onClick={() => handleRemoveItem(row.tempId)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>

                      <TableRow
                        onMouseEnter={() => setHoveredTempId(row.tempId)}
                        onMouseLeave={() => setHoveredTempId(null)}
                        sx={{
                          bgcolor: hoverBgColor,
                          transition: 'background-color 0.15s ease',
                          '& td': { pb: 1.5, pt: 0.5, borderBottom: `1px solid ${theme.palette.divider}` },
                        }}
                      >
                        <TableCell colSpan={10} sx={{ pl: 3 }}>
                          <Box pt={0.5}>
                            <Typography
                              variant="caption"
                              fontWeight="bold"
                              color="text.secondary"
                              display="block"
                              gutterBottom
                            >
                              Observação:
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.primary"
                              display="block"
                              sx={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere',
                              }}
                            >
                              {row.observation}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider />

        <Box p={2.5} display="flex" justifyContent="flex-end" alignItems="center">
          <Button
            variant="contained"
            color="success"
            size="large"
            onClick={handleOpenSendConfirmation}
            disabled={isSubmitting || gridItems.length === 0}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Send />}
            sx={{ fontWeight: 'bold', px: 4, height: 45 }}
          >
            Gerar Solicitação
          </Button>
        </Box>
      </Paper>

      {/* MODAL DE CONFIRMAÇÃO DE ENVIO */}
      <Dialog open={showConfirmSendDialog} onClose={() => setShowConfirmSendDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Send color="primary" /> Confirmar Geração de Solicitação
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" gutterBottom>
            Deseja confirmar o envio da Solicitação de Compra para o ERP Sapiens com os itens abaixo?
          </Typography>

          <Box mt={2} p={2} bgcolor={isDark ? 'grey.900' : 'grey.100'} borderRadius={2}>
            <Stack spacing={1}>
              <Typography variant="body2" color="text.primary">
                <b>Empresa de Destino:</b> {selectedCompanyName}
              </Typography>
              <Typography variant="body2" color="text.primary">
                <b>Filial de Destino:</b> {selectedBranchName}
              </Typography>
              <Typography variant="body2" color="text.primary">
                <b>Total de Itens:</b> {gridItems.length} item(ns)
              </Typography>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowConfirmSendDialog(false)} color="inherit" disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmSendToWebservice}
            color="success"
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <Send />}
            sx={{ fontWeight: 'bold', px: 3 }}
          >
            Confirmar e Enviar
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL DE PROCESSAMENTO */}
      <Dialog
        open={isSubmitting}
        disableEscapeKeyDown
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5, p: 1.5 } }}
      >
        <DialogContent
          sx={{
            py: 4,
            px: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 2,
          }}
        >
          <CircularProgress size={56} thickness={4} color="primary" />
          <Box>
            <Typography variant="h6" fontWeight="bold" color="text.primary" gutterBottom>
              Processando no ERP Sapiens...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320, mx: 'auto' }}>
              Aguarde enquanto as solicitações são geradas no sistema Senior e as notificações são despachadas.
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* MODAL DE RESULTADOS */}
      <Dialog
        open={resultModal.open}
        onClose={() => setResultModal(prev => ({ ...prev, open: false }))}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
          <AssignmentTurnedIn /> Retorno do Processamento ERP Sapiens
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 380 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ '& th': { bgcolor: isDark ? 'grey.900' : 'grey.200', fontWeight: 'bold' } }}>
                  <TableCell width={70}>Seq.</TableCell>
                  <TableCell width={130}>Item / Código</TableCell>
                  <TableCell width={110}>Status</TableCell>
                  <TableCell width={130}>N° Solicitação</TableCell>
                  <TableCell>Mensagem / Detalhamento ERP</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {resultModal.results.map(resItem => (
                  <TableRow key={resItem.seqSol} hover>
                    <TableCell sx={{ fontWeight: 'bold' }}>#{resItem.seqSol}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {resItem.itemCode}
                      </Typography>
                      <Chip
                        label={resItem.itemType === 'P' ? 'Produto' : 'Serviço'}
                        size="small"
                        variant="outlined"
                        sx={{ height: 18, fontSize: '0.65rem' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={resItem.status === 'success' ? 'Sucesso' : 'Erro'}
                        size="small"
                        color={resItem.status === 'success' ? 'success' : 'error'}
                        sx={{ fontWeight: 'bold', height: 22, fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: resItem.numSol ? 'primary.main' : 'text.secondary' }}>
                      {resItem.numSol ? `N° ${resItem.numSol}` : '-'}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        color={resItem.status === 'success' ? 'text.secondary' : 'error.main'}
                        fontWeight={resItem.status === 'error' ? 'bold' : 'normal'}
                        sx={{ lineHeight: 1.35, display: 'block' }}
                      >
                        {resItem.message}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary">
            * Os itens gerados com sucesso foram removidos da tabela. Os itens com erro permanecem para correção.
          </Typography>
          <Button
            onClick={() => setResultModal(prev => ({ ...prev, open: false }))}
            variant="contained"
            color="primary"
            sx={{ fontWeight: 'bold', px: 3 }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* CONFIRMAÇÃO DE ALTERAÇÃO DE EMPRESA/FILIAL */}
      <Dialog
        open={showCompanyConfirmDialog}
        onClose={() => setShowCompanyConfirmDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: 'warning.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmber color="warning" /> Atenção
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            A alteração de <b>Empresa</b> ou <b>Filial</b> irá remover todos os itens adicionados na tabela abaixo.
            Deseja continuar?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowCompanyConfirmDialog(false)} color="inherit">
            Cancelar
          </Button>
          <Button onClick={confirmCompanyBranchChange} color="error" variant="contained" autoFocus>
            Confirmar e Limpar
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG DE COMPRA EMERGENCIAL */}
      <Dialog
        open={emergencyAlert.open}
        onClose={() => setEmergencyAlert({ open: false, pendingItem: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: 'warning.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmber color="warning" /> Atenção: Compra Emergencial
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" gutterBottom>
            A data prevista informada (<b>{formatDateToPtBR(emergencyAlert.pendingItem?.expectedDate || '')}</b>) possui
            um prazo inferior a 10 dias e esta solicitação entrará como <b>COMPRA EMERGENCIAL</b> no ERP Sapiens.
          </Typography>
          <Typography variant="body2" fontWeight="bold" mt={1.5} color="text.primary">
            Deseja realmente continuar e adicionar este item?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => {
              setEmergencyAlert({ open: false, pendingItem: null });
              setTimeout(() => {
                fieldRefs.expectedDate.current?.focus();
              }, 100);
            }}
          >
            Corrigir Data
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              if (emergencyAlert.pendingItem) {
                insertItemToGrid(emergencyAlert.pendingItem);
              }
              setEmergencyAlert({ open: false, pendingItem: null });
            }}
            autoFocus
            sx={{ fontWeight: 'bold' }}
          >
            Sim, Continuar
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL DE AUTENTICAÇÃO ERP */}
      <ErpAuthModal
        open={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        onSuccess={() => {
          showNotification('Credenciais autenticadas com sucesso! Tentando reenviar solicitação...', 'success');
          handleConfirmSendToWebservice();
        }}
      />

      {/* NOTIFICAÇÃO SNACKBAR */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={notification.type} variant="filled" sx={{ fontWeight: 'bold' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
